import { Button } from '@parcel/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth.js';
import {
  type InvitePreview,
  acceptInvite,
  getInvitePreview,
  getInviteRouteState,
} from '../lib/invitations.js';

export const Route = createFileRoute('/invites/$token')({
  component: InviteAcceptRoute,
});

function InviteAcceptRoute() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { session, loading: authLoading, signOut } = useAuth();
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const invitePath = `/invites/${encodeURIComponent(token)}`;
  const signInHref = `/signin?redirect=${encodeURIComponent(invitePath)}`;

  useEffect(() => {
    let active = true;

    setLoadingInvite(true);
    setInvite(null);
    setError(null);

    getInvitePreview(token)
      .then((nextInvite) => {
        if (active) setInvite(nextInvite);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load invitation.');
      })
      .finally(() => {
        if (active) setLoadingInvite(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const state = invite ? getInviteRouteState(invite, session?.user.email ?? null) : null;

  async function accept() {
    setAccepting(true);
    setError(null);

    try {
      const result = await acceptInvite(token);
      await navigate({ to: result.destination });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to accept invitation.');
    } finally {
      setAccepting(false);
    }
  }

  return (
    <main className="grid min-h-svh place-items-center bg-muted p-6">
      <section className="w-full max-w-md rounded-lg border bg-background p-6 shadow-sm">
        {loadingInvite || authLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="size-4 animate-spin" />
            Loading invitation
          </div>
        ) : invite ? (
          <div className="space-y-5">
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm">Workspace invitation</p>
              <h1 className="font-semibold text-2xl tracking-normal">{invite.workspace.name}</h1>
              <p className="text-muted-foreground text-sm">Invited email: {invite.email}</p>
            </div>

            {state === 'sign-in' ? (
              <Button asChild className="w-full">
                <a href={signInHref}>Sign in to accept</a>
              </Button>
            ) : null}

            {state === 'accept' ? (
              <Button className="w-full" onClick={() => void accept()} disabled={accepting}>
                {accepting ? 'Accepting...' : 'Accept invitation'}
              </Button>
            ) : null}

            {state === 'wrong-email' ? (
              <div className="space-y-3">
                <p className="text-destructive text-sm">
                  Sign in as {invite.email} to accept this invitation.
                </p>
                <Button type="button" variant="outline" onClick={() => void signOut()}>
                  Sign out
                </Button>
              </div>
            ) : null}

            {state === 'unavailable' ? (
              <p className="text-muted-foreground text-sm">
                This invitation is {invite.status.toLowerCase()}.
              </p>
            ) : null}

            {error ? <p className="text-destructive text-sm">{error}</p> : null}
          </div>
        ) : (
          <p className="text-destructive text-sm">{error ?? 'Unable to load invitation.'}</p>
        )}
      </section>
    </main>
  );
}
